import React, { useState } from 'react'
import { Outlet, NavLink, Link, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  FileText,
  Users,
  BarChart3,
  Settings,
  Menu,
  X,
  Bell,
  Search,
  ChevronDown,
  LogOut,
  User,
  AlertCircle,
  CheckCircle,
  XCircle,
  Info
} from 'lucide-react'
import { useApp } from '../context/AppContext'

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Invoices', href: '/invoices', icon: FileText },
  { name: 'Clients', href: '/clients', icon: Users },
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'Settings', href: '/settings', icon: Settings },
]

export default function Layout() {
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const [userMenuOpen, setUserMenuOpen] = useState(false)
  const [notificationOpen, setNotificationOpen] = useState(false)
  const { settings, notifications, inbox, markAsRead, markAllAsRead, user, signOut } = useApp()
  const location = useLocation()
  const navigate = useNavigate()

  const getPageTitle = () => {
    const path = location.pathname
    if (path.includes('/invoices/new')) return 'New Invoice'
    if (path.includes('/invoices/') && path.includes('/edit')) return 'Edit Invoice'
    if (path.includes('/invoices/') && path.includes('/view')) return 'Invoice Details'
    const navItem = navigation.find(item => path.startsWith(item.href))
    return navItem ? navItem.name : 'Dashboard'
  }

  const handleSignOut = async () => {
    await signOut()
    navigate('/auth')
  }

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle size={18} className="text-green-500" />
      case 'error':
        return <XCircle size={18} className="text-red-500" />
      case 'warning':
        return <AlertCircle size={18} className="text-yellow-500" />
      default:
        return <Info size={18} className="text-blue-500" />
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Close button (mobile) */}
        <button
          className="lg:hidden absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 transition-colors"
          onClick={() => setSidebarOpen(false)}
        >
          <X size={20} className="text-gray-500" />
        </button>

        {/* Logo */}
        <div className="flex items-center gap-3 px-6 py-5 border-b border-gray-100">
          <img src="/logo.png" alt="nCinq Invoice" className="h-10 w-auto" />
          <div>
            <h1 className="font-bold text-gray-900">nCinq Invoice</h1>
            <p className="text-xs text-gray-500">Professional Invoicing</p>
          </div>
        </div>

        {/* Navigation */}
        <nav className="px-3 py-4 space-y-1">
          {navigation.map((item) => {
            const Icon = item.icon
            return (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `sidebar-link ${isActive ? 'active' : ''}`
                }
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={20} />
                <span>{item.name}</span>
              </NavLink>
            )
          })}
        </nav>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-100">
          <div className="flex items-center gap-3 px-3 py-2">
            <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
              <User size={16} className="text-primary-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {settings.businessName}
              </p>
              <div className="flex gap-2 mt-0.5">
                <Link to="/terms" className="text-[10px] text-gray-400 hover:text-primary-600">Terms</Link>
                <span className="text-[10px] text-gray-300">•</span>
                <Link to="/privacy" className="text-[10px] text-gray-400 hover:text-primary-600">Privacy</Link>
              </div>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <header className="sticky top-0 z-30 bg-white border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-3 lg:px-6">
            {/* Mobile menu button */}
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu size={24} className="text-gray-600" />
            </button>

            {/* Page title (desktop) */}
            <h1 className="hidden lg:block text-lg font-semibold text-gray-900">
              {getPageTitle()}
            </h1>

            {/* Search bar */}
            <div className="flex-1 max-w-md mx-4">
              <div className="relative">
                <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search invoices, clients..."
                  className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm focus:bg-white focus:border-primary-500 focus:ring-1 focus:ring-primary-500 transition-all"
                />
              </div>
            </div>

            {/* Right side actions */}
            <div className="flex items-center gap-2">
              {/* Notifications */}
              <div className="relative">
                <button
                  className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={() => setNotificationOpen(!notificationOpen)}
                >
                  <Bell size={20} className="text-gray-600" />
                  {inbox.filter(n => !n.read).length > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full" />
                  )}
                </button>

                {notificationOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setNotificationOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20 animate-scale-in max-h-96 overflow-y-auto">
                      <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                        <span className="font-semibold text-sm text-gray-900">Notifications</span>
                        {inbox.filter(n => !n.read).length > 0 && (
                          <button
                            onClick={() => markAllAsRead()}
                            className="text-xs text-primary-600 hover:text-primary-700 font-medium"
                          >
                            Mark all read
                          </button>
                        )}
                      </div>
                      {inbox.length === 0 ? (
                        <div className="px-4 py-8 text-center text-gray-500 text-sm">
                          <Bell size={24} className="mx-auto mb-2 opacity-20" />
                          No new notifications
                        </div>
                      ) : (
                        <div>
                          {inbox.map((notification) => (
                            <div
                              key={notification.id}
                              className={`px-4 py-3 border-b border-gray-50 transition-colors ${notification.read ? 'bg-white hover:bg-gray-50' : 'bg-blue-50/30 hover:bg-blue-50/50'}`}
                              onClick={() => {
                                if (!notification.read) markAsRead(notification.id)
                                if (notification.link) {
                                  setNotificationOpen(false)
                                  navigate(notification.link)
                                }
                              }}
                              role={notification.link ? "button" : "presentation"}
                              style={{ cursor: notification.link ? 'pointer' : 'default' }}
                            >
                              <div className="flex items-start gap-3">
                                <div className="mt-0.5 shrink-0">
                                  {getNotificationIcon(notification.type)}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className={`text-sm ${notification.read ? 'text-gray-600' : 'text-gray-900 font-medium'}`}>
                                    {notification.message}
                                  </p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {notification.createdAt ? new Date(notification.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                                  </p>
                                </div>
                                {!notification.read && (
                                  <div className="w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0 mt-1.5" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* User menu */}
              <div className="relative">
                <button
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                >
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                    <User size={16} className="text-primary-600" />
                  </div>
                  <ChevronDown size={16} className="text-gray-500" />
                </button>

                {userMenuOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-10"
                      onClick={() => setUserMenuOpen(false)}
                    />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-1 z-20 animate-scale-in">
                      <Link
                        to="/profile"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <User size={16} />
                        Profile
                      </Link>
                      <Link
                        to="/settings"
                        className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                        onClick={() => setUserMenuOpen(false)}
                      >
                        <Settings size={16} />
                        Settings
                      </Link>
                      <hr className="my-1" />
                      <button
                        onClick={handleSignOut}
                        className="w-full flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                      >
                        <LogOut size={16} />
                        Sign Out
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="p-4 lg:p-6 pb-24 md:pb-6">
          <Outlet />
        </main>
      </div>

      {/* Notification Toasts */}
      <div className="fixed bottom-20 md:bottom-4 right-4 z-50 space-y-2">
        {notifications.map((notification) => (
          <div
            key={notification.id}
            className="flex items-center gap-3 px-4 py-3 bg-white rounded-lg shadow-lg border border-gray-100 animate-slide-up max-w-sm"
          >
            {getNotificationIcon(notification.type)}
            <span className="text-sm text-gray-700 flex-1">{notification.message}</span>
            <button
              onClick={() => {
                // Notification will auto-dismiss after 4 seconds
              }}
              className="p-1 rounded hover:bg-gray-100"
            >
              <X size={14} className="text-gray-400" />
            </button>
          </div>
        ))}
      </div>

      {/* Mobile Bottom Navigation */}
      <nav className="mobile-nav md:hidden">
        {navigation.slice(0, 4).map((item) => {
          const Icon = item.icon
          const isActive = location.pathname.startsWith(item.href)
          return (
            <NavLink
              key={item.name}
              to={item.href}
              className={`mobile-nav-item ${isActive ? 'active' : ''}`}
            >
              <Icon size={22} />
              <span className="text-xs font-medium">{item.name}</span>
            </NavLink>
          )
        })}
        <button
          className="mobile-nav-item"
          onClick={() => setUserMenuOpen(!userMenuOpen)}
        >
          <User size={22} />
          <span className="text-xs font-medium">More</span>
        </button>
      </nav>
    </div>
  )
}
